import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import {
  Box,
  Title,
  ThemeProvider,
  Form,
  Input,
  Textarea,
  Checkbox,
  CMSContent,
  ExpandableContent,
  RatingInput,
  Media,
  Text,
  Layout,
  Level,
  Placeholder,
  Image,
  Link,
  ErrorBoundary,
  MediaQuery,
  Columns,
} from '@deseretbook/react-ui'
import { DeseretBook } from '../../../dbcom/api'
import { Logger } from '../../../dbcom'

const logger = new Logger({ context: 'product/reviews/CreateReviewPage' })

/**
 * This is the Product Preview for the Create a Review component
 * @param {obj} product contains all product info
 * @returns {func} Component
 */
const ProductPreview = ({ product }) => (
  <MediaQuery.Medium>
    {isMediumOrLarger => (

      <Box
        maxWidth={isMediumOrLarger
          ? 25
          : '100%'
        }
      >
        <Media>
          <Media.Figure verticalAlignment="center" size="medium">
            <Link href={`/products/${product.id}`}>
              <Image src={product.master.images[0].productUrl} alt={product.name} />
            </Link>
          </Media.Figure>
          <Media.Content>
            <Title
              title={product.name}
              titleAsComponent="a"
              titleProps={{ href: `/products/${product.id}` }}
              subtitle={product.subtitle}
              subtitleAsComponent="p"
              size={2}
            />
            {
              (product && product.producers) && (
                <Box paddingBottom={1}>
                  <Text display="block" isItalic>
                    {product.producers.map(({ name }) => name).filter(Boolean).join(', ')}
                  </Text>
                </Box>
              )
            }
          </Media.Content>
        </Media>
        { (product && product.description.length > 0) && (
          <ExpandableContent collapsedHeight={5}>
            <CMSContent
              dangerouslySetInnerHTML={{ __html: product.description }}
              itsNotDangerousBecause="It is dangerous!"
            />
          </ExpandableContent>
          )
        }
      </Box>
    )}
  </MediaQuery.Medium>
)

ProductPreview.propTypes = {
  product: PropTypes.shape({
    description: PropTypes.string,
    producers: PropTypes.array,
    subtitle: PropTypes.string,
    name: PropTypes.string,
    master: PropTypes.array,
    id: PropTypes.string,
  }),
}

/**
 * This is the Create Review Component
 * @param {number} productId - the Products Id
 * @returns {func} Component
 */
const CreateReviewPage = ({ productId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [product, setProduct] = useState()
  const [showName, setShowName] = useState(true)
  const [formValue, setFormValue] = useState({
    rating: '',
    title: '',
    review: '',
    name: (window.__USER__ || {}).name || '',
  })
  const productPage = `/products/${productId}`

  /**
   * Fetches the Product and sets the product Variable
   */
  const getProduct = async () => {
    try {
      const productResponse = await DeseretBook
        .product({ id: productId })
        .get()

      if (productResponse) {
        setProduct(productResponse)
      }
    } catch (error) {
      // Fails silently, It's not necessary to see ProductReviewPreview
      logger.error(`Error obtaining product info: ${error}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Sets the formValue state on input change
   * @param {obj} name - Name of the input
   * @param {string} value - The value returned by input
   */
  const handleChange = ({ name, value }) => {
    setFormValue({ ...formValue, [name]: value })
  }

  /**
   * Submits the review. Redirects to product page on success.
   * @param {obj} event - the event object
   */
  const handleSubmit = async (event) => {
    if (!window.__USER__) {
      Turbolinks.visit(productPage)
    }

    event.preventDefault()
    setIsSubmitting(true)

    try {
      const userId = Number(window.__USER__.id)

      await DeseretBook
        .product({ id: productId })
        .review()
        .create({
          ...formValue,
          userId,
          name: showName
            ? formValue.name
            : null,
        })

      // Prevents resubmitting another form after initial submit
      Turbolinks.clearCache()
      Turbolinks.visit(productPage)
    } catch (error) {
      logger.error(`Error while submitting: ${error}`)
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    getProduct()
  }, [])

  return (
    <ThemeProvider>
      <Layout>
        {!!(product || isLoading) && (
          <ErrorBoundary failSilently>
            <Layout.Aside>
              <Layout.Section>
                {
                  isLoading
                    ? (
                      <Placeholder>
                        <Box minWidth={14}>
                          <Media>
                            <Media.Figure verticalAlignment="center" size="large">
                              <Placeholder.Block width={14} height={8} />
                            </Media.Figure>
                            <Media.Content>
                              <Placeholder.Block marginBottom={1.5} width={8} height={2} />
                              <Placeholder.Text lines={2} />
                            </Media.Content>
                          </Media>
                          <Placeholder.Text lines={5} />
                        </Box>
                      </Placeholder>
                      )
                    : <ProductPreview product={product} />
                }
              </Layout.Section>
            </Layout.Aside>
          </ErrorBoundary>
        )}
        <Layout.Content>
          <Layout.Section>
            <Title
              title="Leave us a review"
              size={1}
              titleAsComponent="h1"
            />
            <Form isSubmitting={isSubmitting} onSubmit={handleSubmit}>
              <RatingInput
                name="rating"
                aria-label="How would you rate this product on a scale of 1 to 5?"
                onChange={({ target }) => handleChange(target)}
                selectedValue={formValue.rating}
                required
                removeHint
                size="medium"
              />
              <Columns>
                <Columns.Column size={{ small: 6, large: 4 }}>
                  <Box maxWidth={25}>
                    <Input
                      name="name"
                      type="text"
                      label="Display Name"
                      required={showName}
                      disabled={!showName}
                      hint={showName
                        ? 'The name to be displayed with your review'
                        : 'Your review will be completely anonymous'
                      }
                      value={showName
                        ? formValue.name
                        : ''
                      }
                      maxLength={30}
                      onChange={({ target }) => handleChange(target)}
                    />
                  </Box>
                </Columns.Column>
                <Columns.Column size={{ small: 6, large: 8 }}>
                  <Checkbox
                    onChange={() => setShowName(!showName)}
                    name="anonymous"
                    value={showName}
                    checked={showName}
                    removeHint
                  >
                    Show my name with review?
                  </Checkbox>
                </Columns.Column>
              </Columns>
              <Input
                name="title"
                type="text"
                label="Add a Title"
                hint="What's the first thing others should know?"
                value={formValue.title}
                required
                maxLength={50}
                onChange={({ target }) => handleChange(target)}
              />
              <Textarea
                name="review"
                label="My Review"
                required
                hint="What did you think about the product? Describe your experience."
                value={formValue.review}
                onChange={({ target }) => handleChange(target)}
              />
              <Level>
                <Level.Right>
                  <Form.SubmitButton color="action">Submit</Form.SubmitButton>
                </Level.Right>
              </Level>
            </Form>
          </Layout.Section>
        </Layout.Content>
      </Layout>
    </ThemeProvider>
  )
}

export default CreateReviewPage

CreateReviewPage.propTypes = {
  productId: PropTypes.number.isRequired,
}
